'use client';

import { isNumber } from '@/types';
import { isEmpty } from '@/utils/fp';
import { useCallback, useEffect, useState } from 'react';

const DEFAULT_LIMIT = 10;

/**
 * @param {function useQuery(offset:number, limit: number): { data: unknown, error: unknown }} useQuery - a fetch function that should return a data object with { data: [] and count: number }.
 * @param {string} key - a unique property on the returned object. For instance, within a returned array: [{ projectId: 1, title: 'Big Project' }, { projectId: 2, title: 'Big Project' }], the key should be 'projectId'.
 * @param {number=} [limit=10] - the pagination limit.
 */
export const useLoadMore = <T extends Record<string, any>>(
    useQuery: (
        offset: number,
        limit: number,
        userId: string | undefined,
        surveyKey: string | undefined,
        reCaching: boolean | undefined,
        disableRedirects: boolean | undefined,
    ) => { data: unknown; error: unknown; isLoading: boolean },
    key: string,
    limit = DEFAULT_LIMIT,
    userId?: string,
    surveyKey?: string,
    reCaching?: boolean,
    disableRedirects?: boolean,
): {
    error: unknown;
    loadMore: () => void;
    isLoading: boolean;
    isLoadingMore: boolean;
    shouldShowLoadMore: boolean;
    data: T[] | undefined;
} => {
    const [allData, setAllData] = useState<Map<string, T>>();
    const [offset, setOffset] = useState<number>(0);
    const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
    const { data, error, isLoading } = useQuery(
        offset,
        limit,
        userId,
        surveyKey,
        reCaching,
        disableRedirects,
    );

    const isValidResponse = (d: unknown): d is { data: []; count: number } =>
        d instanceof Object &&
        'data' in d &&
        'count' in d &&
        Array.isArray(d.data) &&
        Number.isFinite(d.count);

    const maxOffset = (d: unknown) => (isValidResponse(d) ? d.count : 0);
    const newOffset = (currentOffset: number, limit: number) => currentOffset + limit;
    const getValidNewOffset = (newOffset: number, maxOffset: number) => {
        return newOffset < maxOffset ? newOffset : undefined;
    };

    const combineResponse = useCallback(
        (data: T[]) => {
            if (isEmpty(allData) && isValidResponse(data)) {
                setAllData(
                    new Map(
                        data.data.map((d: T) => [key in d ? String(d[key]) : 'invalid key', d]),
                    ),
                );
            } else if (isValidResponse(data)) {
                const combinedData = new Map(allData);
                data.data.forEach((d: T) =>
                    combinedData.set(key in d ? String(d[key]) : 'invalid key', d),
                );

                setTimeout(() => {
                    setAllData(combinedData);
                    setIsLoadingMore(false);
                }, 500);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [key, allData?.size],
    );

    useEffect(() => {
        if (isNumber(offset)) {
            combineResponse(data as T[]);
        }
    }, [offset, data, combineResponse]);

    const loadMore = () => {
        if (isLoadingMore) return;
        const validNewOffset = getValidNewOffset(newOffset(offset, limit), maxOffset(data));
        if (validNewOffset) {
            setIsLoadingMore(true);
            setOffset(validNewOffset);
        }
    };

    const hasMoreSurveysThanTheLimit = (d: unknown) => maxOffset(d) > limit;
    const hasLoadedAllSurveys = (d: unknown) => offset + limit > maxOffset(d);

    const shouldShowLoadMore = (d: unknown) =>
        hasMoreSurveysThanTheLimit(d) && !hasLoadedAllSurveys(d);

    const allDataAsArray = (d: Map<string, T>) => Array.from(d, ([, val]) => val);

    return {
        error,
        loadMore,
        isLoading,
        isLoadingMore,
        shouldShowLoadMore: shouldShowLoadMore(data),
        data: allData ? allDataAsArray(allData) : undefined,
    };
};
